import { NameWithId } from "@/components/EvaluationHistory/ControlBar/HistoryFilter/types";
import PaginatedSelect, {
  SelectedValue,
} from "@/components/ui/PaginatedSelect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import useActiveWorkspace from "@/hooks/useActiveWorkspace";
import { cn } from "@/lib/utils";
import { DatasetGroup } from "@/queries/datasets/types";
import { useGetDatasetGroups } from "@/queries/datasets/useGetDatasetGroups";
import { truncate } from "@/utils/strings";
import { UseQueryResult } from "@tanstack/react-query";
import { AlertTriangleIcon, ExternalLink, Search, X } from "lucide-react";
import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import { useCallback, useMemo, useRef, useState } from "react";

const DatasetSelect = observer(
  ({
    isReadOnly,
    datasetId,
    selectedDatasetName,
    setSelectedDatasetName,
  }: {
    isReadOnly?: boolean;
    datasetId: string;
    selectedDatasetName: string | undefined;
    setSelectedDatasetName: (name: string) => void;
  }) => {
    const [searchQuery, setSearchQuery] = useState("");
    const searchInputRef = useRef<HTMLInputElement>(null);

    const DatasetQuery = (
      _page: number,
    ): UseQueryResult<
      { dataset_groups: NameWithId[]; total: number; pages: number },
      unknown
    > | null => {
      const result = useGetDatasetGroups({
        search: searchQuery,
        currentPage: _page,
      });

      if (!result.data)
        return result as unknown as UseQueryResult<
          { dataset_groups: NameWithId[]; total: number; pages: number },
          unknown
        > | null;

      const validDatasetGroups = result.data.dataset_groups.filter(
        (group: DatasetGroup) => {
          const versions =
            group.datasets
              ?.map(({ version_number }) => version_number)
              ?.filter((version) => version > 0) || [];
          return versions.length > 0;
        },
      );

      return {
        ...result,
        data: {
          dataset_groups: validDatasetGroups.map((group) => ({
            id: group.id,
            name: group.name,
          })),
          total: result.data.total,
          pages: Math.ceil(result.data.total / (result.pageSize || 10)),
        },
      } as unknown as UseQueryResult<
        { dataset_groups: NameWithId[]; total: number; pages: number },
        unknown
      > | null;
    };

    const renderDatasetItem = useCallback((datasetItem: NameWithId) => {
      return datasetItem.name;
    }, []);

    const handleValueChange = useCallback(
      (value: SelectedValue<NameWithId>) => {
        runInAction(() => {
          setSelectedDatasetName(value.item.name);
        });
      },
      [setSelectedDatasetName],
    );

    const handleClear = useCallback(() => {
      runInAction(() => {
        setSelectedDatasetName("");
      });
    }, [setSelectedDatasetName]);

    const handleSearchChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
      },
      [],
    );

    const filterComponent = useMemo(
      () => (
        <div className="px-2 py-2">
          <div className="relative">
            <Input
              ref={searchInputRef}
              placeholder="Search datasets..."
              value={searchQuery}
              onChange={handleSearchChange}
              className="h-8 pl-8"
              autoFocus
            />
            <Search className="pointer-events-none absolute left-2 top-2 h-4 w-4 text-gray-500" />
            {searchQuery && (
              <Button
                variant="ghost"
                size="tinyIcon"
                onClick={() => {
                  setSearchQuery("");
                  setTimeout(() => {
                    searchInputRef.current?.focus();
                  }, 0);
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      ),
      [searchQuery, handleSearchChange],
    );

    const activeWorkspace = useActiveWorkspace()?.id;

    const isValidReference = !!(
      selectedDatasetName && selectedDatasetName.trim()
    );

    const InvalidReferenceMessage = () => (
      <div className="mb-2 text-sm text-red-600">Invalid dataset reference</div>
    );

    const getDatasetUrl = () =>
      `/workspace/${activeWorkspace}/dataset-groups/${selectedDatasetName}/dataset/${datasetId}/`;

    const getCommonClasses = (isValid: boolean) =>
      isValid ? "bg-white" : "border-red-300 bg-red-50";

    if (isReadOnly && selectedDatasetName) {
      return (
        <div>
          {!isValidReference && <InvalidReferenceMessage />}
          <Button
            variant="outline"
            className={`flex w-full items-center justify-between ${getCommonClasses(
              isValidReference,
            )} ${
              isValidReference
                ? "hover:bg-gray-50"
                : "cursor-not-allowed hover:bg-red-100"
            }`}
            onClick={() => {
              if (isValidReference) {
                window.open(getDatasetUrl(), "_blank");
              }
            }}
            title={isValidReference ? undefined : "Invalid dataset reference"}
          >
            {selectedDatasetName && (
              <span className={isValidReference ? "" : "text-red-600"}>
                {truncate(selectedDatasetName, 20)}
              </span>
            )}
            {isValidReference ? (
              <ExternalLink className="ml-2 h-4 w-4 opacity-70" />
            ) : (
              <AlertTriangleIcon className="ml-2 h-4 w-4 text-red-600" />
            )}
          </Button>
        </div>
      );
    }

    const selectedValue = selectedDatasetName
      ? {
          id: selectedDatasetName,
          item: {
            id: selectedDatasetName,
            name: selectedDatasetName,
          },
        }
      : undefined;

    return (
      <div className="w-full">
        {!isValidReference && selectedDatasetName && (
          <InvalidReferenceMessage />
        )}
        <PaginatedSelect<
          NameWithId,
          { dataset_groups: NameWithId[]; total: number; pages: number }
        >
          query={DatasetQuery}
          renderItem={renderDatasetItem}
          getItemValue={(item) => item.id.toString()}
          placeholder="Select a dataset"
          onValueChange={handleValueChange}
          value={selectedValue}
          className={cn(
            "w-full whitespace-nowrap",
            getCommonClasses(isValidReference || !selectedDatasetName),
          )}
          disabled={isReadOnly}
          onRemove={selectedDatasetName ? handleClear : undefined}
          extractData={(data) => ({
            data: data.dataset_groups,
            totalPages: data.pages,
          })}
          filterComponent={filterComponent}
        />
      </div>
    );
  },
);

export default DatasetSelect;
